import { type Dictionary, en } from './en.js';
import { km } from './km.js';

/**
 * The i18n layer (CLAUDE.md §6.2). Every user-facing string in the storefront,
 * the admin, the bot and the notifications resolves through `t()`. Khmer is the
 * customer default; the merchant picks the shop's language.
 *
 * Vietnamese never appears here — it is the developer's working language only.
 */

export type Locale = 'km' | 'en';
export const LOCALES: readonly Locale[] = ['km', 'en'];
export const DEFAULT_LOCALE: Locale = 'km';

const DICTIONARIES: Record<Locale, Dictionary> = { en, km };

export { en, km };
export type { Dictionary };

/**
 * Dot-separated key paths into the dictionary, derived from the English
 * object, so `t(locale, 'checkout.placeOder')` fails to compile.
 */
type PathsOf<T, Prefix extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${Prefix}${K}`
    : T[K] extends Record<string, unknown>
      ? PathsOf<T[K], `${Prefix}${K}.`>
      : never;
}[keyof T & string];

export type TranslationKey = PathsOf<Dictionary>;

export type TranslationParams = Record<string, string | number>;

function lookup(dictionary: Dictionary, key: string): string | undefined {
  let node: unknown = dictionary;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

export function interpolate(template: string, params: TranslationParams = {}): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export function t(locale: Locale, key: TranslationKey, params?: TranslationParams): string {
  const dictionary = DICTIONARIES[locale] ?? DICTIONARIES[DEFAULT_LOCALE];
  /**
   * Fall back to English rather than rendering the raw key: a Khmer customer
   * seeing an English sentence is recoverable; seeing `checkout.placeOrder` is
   * not.
   */
  const template = lookup(dictionary, key) ?? lookup(en, key);
  if (template === undefined) return key;
  return interpolate(template, params);
}

/** Bound translator, so components do not thread `locale` through every call. */
export function createTranslator(locale: Locale) {
  return (key: TranslationKey, params?: TranslationParams) => t(locale, key, params);
}
export type Translator = ReturnType<typeof createTranslator>;

export function isLocale(value: unknown): value is Locale {
  return value === 'km' || value === 'en';
}

/**
 * Resolve the locale for a request. Order matters: an explicit choice beats a
 * Telegram hint, which beats the shop's default, which beats Khmer.
 */
export function resolveLocale(options: {
  explicit?: string | null;
  telegramLanguageCode?: string | null;
  shopDefault?: string | null;
}): Locale {
  if (isLocale(options.explicit)) return options.explicit;
  const tg = options.telegramLanguageCode?.slice(0, 2).toLowerCase();
  if (tg === 'km') return 'km';
  if (tg === 'en') return 'en';
  if (isLocale(options.shopDefault)) return options.shopDefault;
  return DEFAULT_LOCALE;
}

/** Order status labels come up in every surface; give them a direct helper. */
export function orderStatusLabel(locale: Locale, status: keyof Dictionary['order']['status']): string {
  return t(locale, `order.status.${status}` as TranslationKey);
}

export function errorMessage(locale: Locale, code: string): string {
  const key = `errors.${code}` as TranslationKey;
  const message = t(locale, key);
  return message === key ? t(locale, 'errors.internal') : message;
}
